import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from './Button';

interface HeroSectionProps {
  title: string;
  subtitle?: string;
  backgroundImage: string;
  buttonText?: string;
  buttonLink?: string;
  height?: string;
  overlay?: boolean;
  overlayOpacity?: number;
  centered?: boolean;
  children?: React.ReactNode;
}

const HeroContainer = styled.section<{
  $backgroundImage: string;
  $height: string;
  $overlay: boolean;
  $overlayOpacity: number;
}>`
  position: relative;
  width: 100%;
  height: ${({ $height }) => $height};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: var(--text-primary);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${({ $backgroundImage }) => $backgroundImage});
    background-size: cover;
    background-position: center;
    z-index: -2;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${({ $overlay, $overlayOpacity }) => 
      $overlay ? `rgba(0, 0, 0, ${$overlayOpacity})` : 'transparent'};
    z-index: -1;
  }
`;

const HeroContent = styled.div<{ $centered: boolean }>`
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
  margin: 0 auto;
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
  z-index: 1;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const HeroSubtitle = styled(motion.p)<{ $centered: boolean }>`
  font-size: clamp(1rem, 2vw, 1.2rem);
  max-width: 600px;
  margin: ${({ $centered }) => $centered ? '0 auto 2rem' : '0 0 2rem'};
  line-height: 1.6;
`;

const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  subtitle,
  backgroundImage,
  buttonText,
  buttonLink,
  height = '100vh',
  overlay = true,
  overlayOpacity = 0.8,
  centered = true,
  children,
}) => {
  return (
    <HeroContainer 
      $backgroundImage={backgroundImage}
      $height={height}
      $overlay={overlay}
      $overlayOpacity={overlayOpacity}
    >
      <HeroContent $centered={centered}>
        <HeroTitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {title}
        </HeroTitle>
        
        {subtitle && (
          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            $centered={centered}
          >
            {subtitle}
          </HeroSubtitle>
        )}
        
        {buttonText && buttonLink && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Button variant="primary" size="large" to={buttonLink}>
              {buttonText}
            </Button>
          </motion.div>
        )}
        
        {children}
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection; 